import { HTMLAttributes, PropsWithChildren, useCallback } from 'react';
import { useEffectOnce } from 'react-use';
import arrowLeft from '~/assets/icons/arrow_left.svg';
import { rage, useInputAction, useObservable } from '~/rage';
import { InputAction } from '~/shared';
import { Button, ButtonColors, ButtonHeight } from '../Button';
import { Text } from '../Text';
import {
	StyledButtonContainer,
	StyledButtonContent,
	StyledContainer,
	StyledListContainer,
	StyledTopContainer,
} from './ShopList.styled';

export interface IShopListProps {
	icon: string;
	title: string;
	handleBack: () => void;
	handleSelect: (index: number | null) => void;
	activeIndex: number | null;
	maxIndex: number;
	handleCheckInstall: (index: number) => boolean;
}

const ShopList = ({
	children,
	icon,
	title,
	handleBack,
	handleSelect,
	activeIndex,
	maxIndex,
	handleCheckInstall,
	...props
}: PropsWithChildren & HTMLAttributes<HTMLDivElement> & IShopListProps) => {
	const modalState = useObservable(rage.modal.getCurrentModal());

	const findNextAvailable = useCallback(
		(start: number, step: -1 | 1): number | null => {
			let index = start;
			for (let i = 0; i <= maxIndex; i++) {
				if (!handleCheckInstall(index)) return index;
				index += step;
				if (index > maxIndex) index = 0;
				if (index < 0) index = maxIndex;
			}
			return null;
		},
		[handleCheckInstall, maxIndex],
	);

	useInputAction(InputAction.CommonMenuNavUp, () => {
		if (modalState) {
			return;
		}

		const start =
			activeIndex === null
				? maxIndex
				: (activeIndex - 1 + maxIndex + 1) % (maxIndex + 1);
		const next = findNextAvailable(start, -1);
		handleSelect(next);
	});

	useInputAction(InputAction.CommonMenuNavDown, () => {
		if (modalState) {
			return;
		}

		const start = activeIndex === null ? 0 : (activeIndex + 1) % (maxIndex + 1);
		const next = findNextAvailable(start, 1);
		handleSelect(next);
	});

	useEffectOnce(() => {
		return () => {
			handleSelect(null);
		};
	});

	return (
		<StyledContainer {...props}>
			<StyledTopContainer>
				<img src={icon} />
				<Text>{title}</Text>
				<Text>
					<span>{(activeIndex || 0) + 1}</span> / {maxIndex + 1}
				</Text>
			</StyledTopContainer>
			<StyledListContainer tabIndex={-1}>{children}</StyledListContainer>
			<StyledButtonContainer>
				<Button
					color={ButtonColors.Default}
					height={ButtonHeight.Height50}
					onClick={handleBack}
				>
					<StyledButtonContent>
						<img src={arrowLeft} />
						<Text>Вернуться назад</Text>
					</StyledButtonContent>
				</Button>
			</StyledButtonContainer>
		</StyledContainer>
	);
};

export { ShopList };
